import { Box, Text } from '@factorialco/deprecated-design-system'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import { GetFeatureAvailabilitiesDocument } from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import { dateBackendToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { isFeatureAvailabilityEnabled } from 'modules/core/lib/isFeatureAvailabilityEnabled'

import useCurrentIncidenceCompany from 'modules/bookkeepers/hooks/useIncidenceCompany'
import { useCurrentIncidence } from 'modules/bookkeepers/lib/CurrentIncidenceContext'
import { useBookkeeperView } from 'modules/bookkeepers/lib/bookkeeperViewContext'
import { terminationReasonTranslation } from 'modules/bookkeepers/lib/dynamicTranslations'
import type IncidenceDetail from 'modules/bookkeepers/store/models/IncidenceDetail'

import DetailLine from 'modules/payroll_bundle/pages/IncidenceDetailPage/_DetailLine'

interface Props {
  incidenceDetailModel: IncidenceDetail
}

const TerminationInfo = ({ incidenceDetailModel }: Props) => {
  const terminationReason =
    terminationReasonTranslation(incidenceDetailModel.attributes.termination_reason) ||
    incidenceDetailModel.attributes.termination_reason
  const incidence = useCurrentIncidence()
  const company = useCurrentIncidenceCompany(incidence)

  const isBookkeeperView = useBookkeeperView()
  const { loading, data } = useQuery(GetFeatureAvailabilitiesDocument, {
    fetchPolicy: 'cache-first',
    variables: {
      modules: [AvailableModules.timeoff],
      companyId: company.id,
    },
    context: {
      isBookkeeperView,
    },
  })

  const featureAvailabilities = data?.companies.featureAvailabilities ?? []
  const isTimeoffEnabled = isFeatureAvailabilityEnabled(
    featureAvailabilities,
    Features.timeoff_management
  )

  if (loading) return <></>

  return (
    <Box gap="s16">
      {incidenceDetailModel.has('terminated_on') && (
        <DetailLine
          component="li"
          title={i18n.t('bookkeepers.incidence.details.effective_date')}
          value={dateBackendToUI(incidenceDetailModel.attributes.terminated_on)}
        />
      )}
      {terminationReason && (
        <DetailLine
          component="li"
          title={i18n.t('bookkeepers.incidence.details.termination_reason')}
          value={terminationReason}
        />
      )}
      {incidenceDetailModel.has('termination_observations') && (
        <DetailLine
          component="li"
          title={i18n.t('bookkeepers.incidence.details.termination_observations')}
          value={incidenceDetailModel.attributes.termination_observations}
        />
      )}
      {isTimeoffEnabled && incidenceDetailModel.has('remaining_holidays') && (
        <Box gap="s8" paddingTop="s24">
          <Text.Normal size="200" color="grey800" weight="bold">
            {i18n.t('bookkeepers.incidence.details.sections.remaining_absences')}
          </Text.Normal>

          {incidenceDetailModel.attributes.remaining_holidays.map(
            (allowance: { name: string; available_days: string }) => {
              return (
                <DetailLine
                  key={allowance.name}
                  component="li"
                  title={allowance.name}
                  value={allowance.available_days}
                />
              )
            }
          )}
        </Box>
      )}
    </Box>
  )
}

export default TerminationInfo
